(() => {
  // =========================
  // Compare (up to 4 products) + Multi-step Undo/Redo (stacks)
  // + Dynamic Metafields rows + Collapsible bar (center arrow)
  // + Compare button last + Outline + Add-current yellow
  // + ✅ Print button (compare page) + Print CSS (table-like)
  // + ✅ Hide variants when only "Default Title"
  // + ✅ When only 1 item: show "+ placeholder" that is clickable (go back / open last page)
  // =========================

  // ---------- helpers ----------
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const money = (cents) => {
    try {
      return new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS" }).format((cents || 0) / 100);
    } catch {
      return ((cents || 0) / 100).toFixed(2) + " ₪";
    }
  };

  // ---------- storage ----------
  const KEY = "cmp:v1";
  const LAST_PAGE_KEY = "cmp:last_page:v1";

  const saveLastPage = () => {
    try {
      const path = window.location.pathname || "";
      if (path.includes("/pages/compare")) return;
      sessionStorage.setItem(LAST_PAGE_KEY, window.location.href);
    } catch {}
  };

  const goBackOrHome = () => {
    try {
      const url = sessionStorage.getItem(LAST_PAGE_KEY);
      if (url && url !== window.location.href) {
        window.location.href = url;
        return;
      }
    } catch {}
    window.location.href = "/";
  };

  const load = () => {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || !Array.isArray(parsed.items)) return { items: [] };
      return { items: parsed.items.filter(Boolean) };
    } catch {
      return { items: [] };
    }
  };

  const save = (state) => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch {}
    window.dispatchEvent(new CustomEvent("cmp:updated"));
  };

  // ---------- selection rules ----------
  const MAX = 4;
  const desiredCols = (count) => {
    if (count <= 2) return 2;
    if (count === 3) return 3;
    return 4;
  };
  const isSameHandle = (a, b) => String(a || "").trim() === String(b || "").trim();

  // ---------- toast ----------
  let toastTimer = null;
  const toast = (text) => {
    let el = $("#cmp-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "cmp-toast";
      el.style.cssText = `
        position: fixed; left: 50%; bottom: 92px; transform: translateX(-50%);
        background: rgba(0,0,0,0.85); color:#fff; padding:10px 14px;
        border-radius: 12px; z-index: 999999; font-weight: 900; font-size: 14px;
        opacity: 0; transition: opacity .2s ease;
        max-width: min(92vw, 420px); text-align:center;
      `;
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.style.opacity = "1";
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(() => (el.style.opacity = "0"), 1600);
  };

  // =========================
  // Multi-step Undo/Redo (stacks)
  // =========================
  const UNDO_STACK_KEY = "cmp:undo_stack:v1";
  const REDO_STACK_KEY = "cmp:redo_stack:v1";
  const MAX_HISTORY = 50;

  const loadStack = (key) => {
    try {
      const raw = sessionStorage.getItem(key);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const saveStack = (key, arr) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(arr));
    } catch {}
  };

  const pushUndo = (state) => {
    const stack = loadStack(UNDO_STACK_KEY);
    stack.push(state);
    if (stack.length > MAX_HISTORY) stack.splice(0, stack.length - MAX_HISTORY);
    saveStack(UNDO_STACK_KEY, stack);
    window.dispatchEvent(new CustomEvent("cmp:undo_updated"));
  };

  const pushRedo = (state) => {
    const stack = loadStack(REDO_STACK_KEY);
    stack.push(state);
    if (stack.length > MAX_HISTORY) stack.splice(0, stack.length - MAX_HISTORY);
    saveStack(REDO_STACK_KEY, stack);
    window.dispatchEvent(new CustomEvent("cmp:redo_updated"));
  };

  const popUndo = () => {
    const stack = loadStack(UNDO_STACK_KEY);
    const st = stack.pop() || null;
    saveStack(UNDO_STACK_KEY, stack);
    window.dispatchEvent(new CustomEvent("cmp:undo_updated"));
    return st;
  };

  const popRedo = () => {
    const stack = loadStack(REDO_STACK_KEY);
    const st = stack.pop() || null;
    saveStack(REDO_STACK_KEY, stack);
    window.dispatchEvent(new CustomEvent("cmp:redo_updated"));
    return st;
  };

  const clearRedoStack = () => {
    try {
      sessionStorage.removeItem(REDO_STACK_KEY);
    } catch {}
    window.dispatchEvent(new CustomEvent("cmp:redo_updated"));
  };

  const canUndo = () => loadStack(UNDO_STACK_KEY).length > 0;
  const canRedo = () => loadStack(REDO_STACK_KEY).length > 0;

  const undo = () => {
    const prev = popUndo();
    if (!prev || !Array.isArray(prev.items)) return false;
    pushRedo(load());
    save({ items: prev.items.filter(Boolean) });
    toast("בוטל");
    return true;
  };

  const redo = () => {
    const next = popRedo();
    if (!next || !Array.isArray(next.items)) return false;
    pushUndo(load());
    save({ items: next.items.filter(Boolean) });
    toast("שוחזר");
    return true;
  };

  const clear = () => {
    const prev = load();
    pushUndo(prev);
    clearRedoStack();
    save({ items: [] });
  };

  // =========================
  // Collapsible Compare Bar (open/close)
  // =========================
  const BAR_COLLAPSE_KEY = "cmp:bar_collapsed:v1";

  const isBarCollapsed = () => {
    try {
      return sessionStorage.getItem(BAR_COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  };

  const setBarCollapsed = (collapsed) => {
    try {
      sessionStorage.setItem(BAR_COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {}
    applyBarCollapsedState();
  };

  const toggleBarCollapsed = () => setBarCollapsed(!isBarCollapsed());

  const applyBarCollapsedState = () => {
    const bar = $("#cmp-bar");
    if (!bar) return;

    const collapsed = isBarCollapsed();
    bar.classList.toggle("is-collapsed", collapsed);

    const handleBtn = bar.querySelector("[data-cmp-handle]");
    if (handleBtn) {
      handleBtn.setAttribute("aria-expanded", collapsed ? "false" : "true");
      const up = handleBtn.querySelector("[data-ico-up]");
      const down = handleBtn.querySelector("[data-ico-down]");
      if (up && down) {
        up.style.display = collapsed ? "block" : "none";
        down.style.display = collapsed ? "none" : "block";
      }
    }
    updateBodyPad();
  };

  // =========================
  // Dynamic Metafields (from product HTML)
  // =========================
  const SKIP_LABEL_PATTERNS = [/all_related_products/i, /related\s*prod/i, /למחוק\s*לעדכון/i];

  const normalizeLabel = (label) => String(label || "").trim();
  const normalizeValue = (value) => String(value || "").replace(/\s+/g, " ").trim();

  const shouldSkipLabel = (label) => {
    const s = normalizeLabel(label);
    if (!s) return true;
    return SKIP_LABEL_PATTERNS.some((re) => re.test(s));
  };

  const renderMetaValue = (val) => {
    const v = normalizeValue(val);
    if (!v) return "-";

    const isUrl = /^https?:\/\//i.test(v);
    const isPdf = /\.pdf(\?|#|$)/i.test(v);

    if (isUrl || isPdf) {
      return `<a href="${escapeHtml(v)}" target="_blank" rel="noopener">פתח קובץ</a>`;
    }
    return escapeHtml(v);
  };

  const META_CACHE_KEY = "cmp:meta_cache:v1";
  const metaCacheLoad = () => {
    try {
      const raw = sessionStorage.getItem(META_CACHE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };
  const metaCacheSave = (cache) => {
    try {
      sessionStorage.setItem(META_CACHE_KEY, JSON.stringify(cache));
    } catch {}
  };

  const fetchMetafieldsFromProductHTML = async (handle) => {
    if (!handle) return {};
    const cache = metaCacheLoad();
    if (cache[handle]) return cache[handle];

    try {
      const html = await fetch(`/products/${handle}`, { credentials: "same-origin" }).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, "text/html");

      const items = Array.from(doc.querySelectorAll(".product__metafield-item"));
      const out = {};

      for (const item of items) {
        const labelEl = item.querySelector(".product__metafield-label");
        const valueEl = item.querySelector(".product__metafield-value");

        const label = normalizeLabel(labelEl?.textContent);
        const value = normalizeValue(valueEl?.textContent);

        if (!label || shouldSkipLabel(label)) continue;
        if (!value) continue;

        if (out[label] == null) out[label] = value; // keep first
      }

      cache[handle] = out;
      metaCacheSave(cache);
      return out;
    } catch {
      cache[handle] = {};
      metaCacheSave(cache);
      return {};
    }
  };

  // =========================
  // ✅ Variant UI rule: hide when only Default Title
  // =========================
  const isOnlyDefaultVariant = (p) => {
    const vs = p?.variants || [];
    if (vs.length !== 1) return false;
    const t = String(vs[0]?.title || "").trim().toLowerCase();
    return t === "default title";
  };

  // =========================
  // Styles injectors (scoped)
  // =========================
  const injectBarStyles = () => {
    if ($("#cmp-bar-style")) return;
    const style = document.createElement("style");
    style.id = "cmp-bar-style";
    style.textContent = `
      :root{ --cmp-bar-h: 0px; }

      /* ✅ prevent bar sitting on text */
      body{ padding-bottom: var(--cmp-bar-h) !important; }

      /* ===== Compare Bar (scoped) ===== */
      #cmp-bar{
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 999998;
        background: rgba(255,255,255,0.96);
        border-top: 1px solid #e5e5e5;
        backdrop-filter: blur(8px);
        padding: 10px;
        direction: rtl;
        box-sizing: border-box;
      }

      #cmp-bar .cmp-bar__inner{
        max-width: 1200px;
        margin: 0 auto;
        display:flex;
        align-items:center;
        justify-content:space-between;
        gap: 10px;
        flex-wrap: wrap;
        position: relative; /* handle anchor */
      }

      #cmp-bar .cmp-bar__left{
        display:flex;
        align-items:center;
        gap:10px;
        flex: 1;
        min-width: 260px;
      }
      #cmp-bar .cmp-bar__title{ font-weight: 900; }
      #cmp-bar .cmp-bar__chips{ display:flex; gap:8px; flex-wrap:wrap; }

      #cmp-bar .cmp-chip{
        display:inline-flex; align-items:center; gap:8px;
        border:1px solid #e5e5e5; background:#fff;
        border-radius: 999px; padding:6px 10px;
        font-weight: 800;
        max-width: min(72vw, 360px);
      }
      #cmp-bar .cmp-chip__img{
        width:22px; height:22px; border-radius: 8px;
        object-fit: cover; border: 1px solid #eee;
        flex: 0 0 auto;
      }
      #cmp-bar .cmp-chip__t{
        white-space: nowrap; overflow:hidden; text-overflow: ellipsis;
        min-width: 0;
      }
      #cmp-bar .cmp-chip__x{
        width:20px; height:20px;
        display:grid; place-items:center;
        border-radius:6px;
        background:#fff;
        border:1px solid #e5e5e5;
        font-size:11px; line-height:1; font-weight:900;
        cursor:pointer;
        padding:0;
        flex: 0 0 auto;
      }

      #cmp-bar .cmp-bar__right{
        display:flex; gap:10px; align-items:center; flex-wrap:wrap;
      }

      #cmp-bar .cmp-btn{
        border:1px solid #dcdcdc;
        background:#f4f6f8;
        padding:10px 12px;
        border-radius:12px;
        cursor:pointer;
        text-decoration:none;
        color:inherit;
        display:inline-flex;
        align-items:center;
        justify-content:center;
        gap:8px;
        font-weight:800;
        box-sizing: border-box;
      }
      #cmp-bar .cmp-btn:hover{ background:#e6eef8; border-color:#1a73e8; }
      #cmp-bar .cmp-btn[aria-disabled="true"]{ opacity:.6; pointer-events:none; }

      /* ✅ Compare button = outline only */
      #cmp-bar .cmp-btn--compare{
        background: transparent !important;
        color: #029ad8 !important;
        border-color: #029ad8 !important;
      }
      #cmp-bar .cmp-btn--compare:hover{
        background: #e6eef8 !important;
      }

      /* ✅ Add-current = yellow */
      #cmp-bar .cmp-btn--yellow{
        background: #FFCB04 !important;
        border-color: #FFCB04 !important;
        color: #111 !important;
      }
      #cmp-bar .cmp-btn--yellow:hover{
        filter: brightness(.96);
      }

      /* ===== Collapsible handle ===== */
      #cmp-bar .cmp-handle{
        position: absolute;
        left: 50%;
        top: -26px;
        transform: translateX(-50%);
        width: 46px;
        height: 28px;
        border-radius: 999px;
        border: 1px solid #e5e5e5;
        background: #fff;
        display: grid;
        place-items: center;
        cursor: pointer;
        box-shadow: 0 10px 22px rgba(0,0,0,.12);
        z-index: 2;

        padding: 0 !important;
        margin: 0 !important;
        min-width: 46px !important;
        max-width: 46px !important;
        min-height: 28px !important;
        max-height: 28px !important;
        line-height: 1 !important;
        appearance: none !important;
        -webkit-appearance: none !important;
      }
      #cmp-bar .cmp-handle svg{
        width: 18px;
        height: 18px;
        display: block;
      }

      /* ===== Collapsed state ===== */
      #cmp-bar.is-collapsed{
        padding-top: 18px;
      }
      #cmp-bar.is-collapsed .cmp-bar__chips,
      #cmp-bar.is-collapsed .cmp-bar__right{
        display: none !important;
      }
      #cmp-bar.is-collapsed .cmp-bar__left{
        width: 100%;
        min-width: 0;
        justify-content: center;
      }
      #cmp-bar.is-collapsed .cmp-bar__title{
        text-align: center;
        width: 100%;
      }

      @media (max-width: 560px){
        #cmp-bar{ padding: 8px; }
        #cmp-bar.is-collapsed{ padding-top: 18px; }
        #cmp-bar .cmp-bar__inner{ gap: 8px; }

        #cmp-bar .cmp-chip{
          display:grid;
          grid-template-columns: 26px 1fr 20px;
          align-items:center;
          gap:8px;
          padding:6px 8px;
          max-width: 100%;
          width: 100%;
        }
        #cmp-bar .cmp-bar__chips{ width: 100%; }
        #cmp-bar .cmp-chip__t{ max-width: 100%; }
        #cmp-bar .cmp-btn{ padding: 10px 10px; }
      }
    `;
    document.head.appendChild(style);
  };

  const injectComparePageStyles = () => {
  if (document.querySelector("#cmp-page-style")) return;

  const style = document.createElement("style");
  style.id = "cmp-page-style";
  style.textContent = `
    /* ===== Compare Page (scoped) ===== */
    .cmp-page{ direction: rtl; }
    .cmp-page__head{
      display:flex; align-items:center; justify-content:space-between;
      gap:12px; flex-wrap:wrap; margin-bottom:14px;
    }
    .cmp-actions{ display:flex; gap:10px; flex-wrap:wrap; }

    .cmp-page .cmp-btn{
      border:1px solid #dcdcdc; background:#f4f6f8; padding:10px 12px;
      border-radius:12px; cursor:pointer; text-decoration:none; color:inherit;
      display:inline-flex; align-items:center; justify-content:center; gap:8px;
      font-weight:800;
    }
    .cmp-page .cmp-btn:hover{ background:#e6eef8; border-color:#1a73e8; }
    .cmp-page .cmp-btn--primary{ background:#029ad8; border-color:#029ad8; color:#fff; }
    .cmp-page .cmp-btn--primary:hover{ filter:brightness(0.95); }
    .cmp-page .cmp-btn[aria-disabled="true"]{ opacity:.6; pointer-events:none; }

    /* hide visit button on screen */
    .cmp-btn--visit{ display:none !important; }

    .cmp-table-wrap{
      border:1px solid #e5e5e5; border-radius:16px; overflow:hidden;
      background:#fff;
    }
    .cmp-table{
      width:100%;
      border-collapse:separate;
      border-spacing:0;
      table-layout:fixed;
    }
    .cmp-table th, .cmp-table td{
      border-bottom:1px solid #eee;
      padding:10px 10px;
      vertical-align:top;
      white-space: normal !important;
      word-break: break-word !important;
      overflow-wrap: anywhere !important;
    }
    .cmp-table tr:last-child th, .cmp-table tr:last-child td{ border-bottom:0; }

    .cmp-rowlabel{
      width:150px;
      font-weight:900;
      background:#fafafa;
    }
    .cmp-table td{ width: calc((100% - 150px) / 4); }

    .cmp-colhead{ display:flex; align-items:flex-start; justify-content:space-between; gap:10px; }
    .cmp-colleft{ display:flex; gap:10px; align-items:center; min-width:0; }
    .cmp-coltext{ min-width:0; display:flex; flex-direction:column; gap:4px; }
    .cmp-thumb{
      width:52px; height:52px; object-fit:cover;
      border-radius:12px; border:1px solid #eee;
      flex: 0 0 auto;
    }
    .cmp-titlelink{
      font-weight:900;
      text-decoration:none;
      color:inherit;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
      max-width: 260px;
    }
    .cmp-vendor{ font-size:12px; opacity:.7; }

    .cmp-xslot{ width:44px; display:flex; justify-content:flex-end; align-items:flex-start; flex: 0 0 auto; }
    .cmp-x{
      width:28px; height:28px; border-radius:8px;
      border:1px solid #e5e5e5; background:#fff; cursor:pointer;
      font-weight:900; font-size:14px; line-height:1;
      display:grid; place-items:center; padding:0;
    }

    .cmp-variant-select{
      width:100%;
      padding:10px;
      border-radius:12px;
      border:1px solid #dcdcdc;
      background:#fff;
    }

    /* description: no clamp */
    .cmp-desc,
    .cmp-desc--collapsed{
      max-height:none !important;
      overflow:visible !important;
      display:block !important;
      -webkit-line-clamp: unset !important;
      -webkit-box-orient: unset !important;
      line-height:1.4;
    }
    .cmp-desc-toggle{ display:none !important; }

    /* mobile layout unchanged */
    @media (max-width: 820px){
      .cmp-table-wrap{ display:none; }
      .cmp-mobile-grid{ display:grid; gap:12px; }
      .cmp-card{ border:1px solid #e5e5e5; border-radius:16px; padding:12px; background:#fff; }
    }
    @media (min-width: 821px){
      .cmp-mobile-grid{ display:none; }
    }
  `;
  document.head.appendChild(style);
};


const printCompareClean = () => {
  const wrap = document.querySelector(".cmp-table-wrap");
  if (!wrap) {
    alert("לא נמצאה טבלת השוואה להדפסה");
    return;
  }

  // משכפלים רק את הטבלה (ללא כפתורים)
  const tableHtml = wrap.outerHTML;

  const logoUrl =
    "https://yashir-group.biz/cdn/shop/files/logo-blue_de604786-f81e-4b3a-9647-bbdc3c41996b.png?v=1737285364&width=200";

  const doc = `<!doctype html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>השוואת מוצרים</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    html, body { margin:0; padding:0; background:#fff; }
    body { font-family: Arial, sans-serif; }

    .print-head{
      display:flex;
      justify-content:space-between;
      align-items:flex-start;
      margin: 0 0 8px 0;
    }
    .print-logo{
      width:200px;
      height:60px;
      object-fit:contain;
    }
    .print-title{
      font-weight:900;
      font-size:16px;
      margin:0;
      padding-top:6px;
    }

    /* הטבלה */
    .cmp-table-wrap{
      border:1px solid #e5e5e5;
      border-radius:12px;
      overflow:hidden;
      background:#fff;
    }
    table{
      width:100%;
      border-collapse:separate;
      border-spacing:0;
      table-layout:fixed;
      font-size:9.5px;
      line-height:1.15;
    }
    th, td{
      border-bottom:1px solid #eee;
      padding:5px 6px;
      vertical-align:top;
      white-space: normal;
      word-break: break-word;
      overflow-wrap: anywhere;
    }
    tr:last-child th, tr:last-child td{ border-bottom:0; }
    th.cmp-rowlabel{
      width:135px;
      font-weight:900;
      background:#fafafa;
    }

    /* תמונה/כותרת בתוך header של מוצר */
    .cmp-thumb{ width:44px !important; height:44px !important; border-radius:10px !important; }
    .cmp-titlelink{
      font-weight:900;
      text-decoration:none;
      color:#000;
      white-space:nowrap;
      overflow:hidden;
      text-overflow:ellipsis;
      max-width: 220px;
      display:inline-block;
    }
    .cmp-vendor{ font-size:11px; opacity:.7; }

    /* להעלים דברים שלא רלוונטיים להדפסה */
    .cmp-xslot, .cmp-x, .cmp-btn, .cmp-actions, .cmp-addslot { display:none !important; }

    /* בלי שבירות עמוד */
    table, thead, tbody, tr, td, th{
      break-inside: avoid;
      page-break-inside: avoid;
    }
    .cmp-table-wrap{
      break-inside: avoid;
      page-break-inside: avoid;
    }
  </style>
</head>
<body>
  <div class="print-head">
    <img class="print-logo" src="${logoUrl}" alt="Yashir Group" />
    <h1 class="print-title">השוואת מוצרים</h1>
  </div>

  ${tableHtml}

  <script>
    // להבטיח שהדפסה תקרה אחרי רינדור
    window.onload = () => {
      setTimeout(() => {
        window.focus();
        window.print();
      }, 50);
    };
  </script>
</body>
</html>`;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.setAttribute("aria-hidden", "true");

  document.body.appendChild(iframe);

  const w = iframe.contentWindow;
  const d = iframe.contentDocument || w.document;

  d.open();
  d.write(doc);
  d.close();

  // ניקוי אחרי ההדפסה
  const cleanup = () => {
    try { iframe.remove(); } catch {}
  };
  w.onafterprint = cleanup;
  setTimeout(cleanup, 15000); // fallback
};



  // =========================
  // Body padding (avoid bar covering content)
  // =========================
  const updateBodyPad = () => {
    const bar = $("#cmp-bar");
    if (!bar) {
      document.documentElement.style.setProperty("--cmp-bar-h", "0px");
      return;
    }
    const visible = bar.style.display !== "none";
    if (!visible) {
      document.documentElement.style.setProperty("--cmp-bar-h", "0px");
      return;
    }
    const h = Math.ceil(bar.getBoundingClientRect().height || 0);
    document.documentElement.style.setProperty("--cmp-bar-h", `${h}px`);
  };

  // =========================
  // add/remove
  // =========================
  const addItem = (item) => {
    const state = load();
    if (!item?.handle) return;

    const exists = state.items.some((x) => isSameHandle(x?.handle, item.handle));
    if (exists) return;

    clearRedoStack();
    pushUndo(state);

    const next = [...state.items, item].slice(0, MAX);
    save({ items: next });

    setBarCollapsed(false);
    toast("נוסף להשוואה");
  };

  const removeItemByHandle = (handle) => {
    if (!handle) return;
    const prev = load();

    clearRedoStack();
    pushUndo(prev);

    const next = prev.items.filter((x) => !isSameHandle(x?.handle, handle));
    save({ items: next });
    toast("הוסר");

    if (document.querySelector("[data-cmp-compare-page]")) {
      const st = load();
      if (!st.items.length) goBackOrHome();
    }
  };

  // =========================
  // Bottom compare bar
  // =========================
  const ensureBar = () => {
    if ($("#cmp-bar")) return;

    const bar = document.createElement("div");
    bar.id = "cmp-bar";
    bar.setAttribute("data-cmp-bar", "");
    bar.innerHTML = `
      <div class="cmp-bar__inner">
        <button class="cmp-handle" type="button" data-cmp-handle aria-expanded="true" aria-label="הצג/הסתר השוואת מוצרים">
          <svg data-ico-up xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M6 15l6-6 6 6"/>
          </svg>
          <svg data-ico-down xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="display:none;">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>

        <div class="cmp-bar__left">
          <div class="cmp-bar__title">השוואת מוצרים</div>
          <div class="cmp-bar__chips" data-cmp-chips></div>
        </div>

        <div class="cmp-bar__right">
          <button class="cmp-btn cmp-btn--yellow" type="button" data-cmp-add-current style="display:none;">
            <span aria-hidden="true" style="display:inline-flex;align-items:center;gap:6px;">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M4 6h6v14H4V6Z"/>
                <path d="M14 4h6v16h-6V4Z"/>
                <path d="M12 12h8"/>
                <path d="M16 8v8"/>
              </svg>
            </span>
            <span<>הוסף מוצר להשוואה></span>
          </button>

          <button class="cmp-btn" type="button" data-cmp-undo aria-disabled="true">בטל</button>
          <button class="cmp-btn" type="button" data-cmp-redo aria-disabled="true">שחזר</button>
          <button class="cmp-btn" type="button" data-cmp-clear>נקה</button>

          <!-- ✅ LAST + outline -->
          <a class="cmp-btn cmp-btn--compare" href="/pages/compare" data-cmp-go aria-disabled="true">השווה</a>
        </div>
      </div>
    `;

    document.body.appendChild(bar);
    injectBarStyles();

    bar.querySelector("[data-cmp-handle]")?.addEventListener("click", () => toggleBarCollapsed());

    bar.querySelector("[data-cmp-clear]")?.addEventListener("click", () => {
      clear();
      toast("נוקה");

      if (document.querySelector("[data-cmp-compare-page]")) {
        const st = load();
        if (!st.items.length) goBackOrHome();
      }
    });

    bar.querySelector("[data-cmp-redo]")?.addEventListener("click", () => {
      if (!canRedo()) return;
      redo();
    });

    bar.querySelector("[data-cmp-undo]")?.addEventListener("click", () => {
      if (!canUndo()) return;
      undo();
    });

    bar.querySelector("[data-cmp-add-current]")?.addEventListener("click", () => {
      const item = getCurrentProductFromPage();
      if (!item?.handle) {
        toast("לא נמצא מוצר בדף להוספה");
        return;
      }
      addItem(item);
    });

    renderBar();
    syncUndoRedoButtons();
    applyBarCollapsedState();

    window.addEventListener("resize", () => updateBodyPad());
  };

  const renderBar = () => {
    const bar = $("#cmp-bar");
    if (!bar) return;

    const state = load();
    const chips = bar.querySelector("[data-cmp-chips]");
    const go = bar.querySelector("[data-cmp-go]");

    bar.style.display = state.items.length ? "block" : "none";
    if (go) go.setAttribute("aria-disabled", state.items.length >= 1 ? "false" : "true");

    if (!chips) return;

    chips.innerHTML = state.items
      .slice(0, MAX)
      .map((it) => {
        const img = it.image ? `<img class="cmp-chip__img" src="${escapeHtml(it.image)}" alt="">` : "";
        return `
          <div class="cmp-chip">
            ${img}
            <span class="cmp-chip__t">${escapeHtml(it.title || it.handle)}</span>
            <button class="cmp-chip__x" type="button" data-cmp-remove="${escapeHtml(it.handle)}">✕</button>
          </div>
        `;
      })
      .join("");

    const addCurrentBtn = bar.querySelector("[data-cmp-add-current]");
    if (addCurrentBtn) {
      const onProductPage = window.location.pathname.includes("/products/");
      const st = load();
      const current = getCurrentProductFromPage();

      const already = current?.handle && st.items.some((x) => isSameHandle(x?.handle, current.handle));
      const shouldShow = onProductPage && st.items.length >= 1 && current?.handle && !already;

      addCurrentBtn.style.display = shouldShow ? "inline-flex" : "none";
    }

    $$("[data-cmp-remove]", chips).forEach((btn) => {
      btn.addEventListener("click", () => {
        const h = btn.getAttribute("data-cmp-remove");
        removeItemByHandle(h);
      });
    });

    applyBarCollapsedState();
    updateBodyPad();
  };

  // ---------- sync header compare button ----------
  const syncAccountCompareButton = () => {
    const btn = document.querySelector("[data-account-compare-btn]");
    if (!btn) return;
    const state = load();
    btn.setAttribute("aria-disabled", state.items.length >= 1 ? "false" : "true");
  };

  // ---------- undo/redo buttons sync ----------
  const syncUndoRedoButtons = () => {
    const undoBar = $("#cmp-bar")?.querySelector("[data-cmp-undo]");
    if (undoBar) undoBar.setAttribute("aria-disabled", canUndo() ? "false" : "true");

    const redoBar = $("#cmp-bar")?.querySelector("[data-cmp-redo]");
    if (redoBar) redoBar.setAttribute("aria-disabled", canRedo() ? "false" : "true");

    const undoPage = $("[data-cmp-undo-page]");
    if (undoPage) undoPage.setAttribute("aria-disabled", canUndo() ? "false" : "true");

    const redoPage = $("[data-cmp-redo-page]");
    if (redoPage) redoPage.setAttribute("aria-disabled", canRedo() ? "false" : "true");
  };

  // ---------- bind product page buttons ----------
  const bindAddButtons = () => {
    $$("[data-cmp-add]").forEach((btn) => {
      if (btn.dataset.cmpBound === "1") return;
      btn.dataset.cmpBound = "1";

      btn.addEventListener("click", () => {
        const item = {
          handle: btn.getAttribute("data-cmp-handle"),
          title: btn.getAttribute("data-cmp-title"),
          image: btn.getAttribute("data-cmp-image"),
          url: btn.getAttribute("data-cmp-url"),
        };
        addItem(item);
      });
    });
  };

  const getCurrentProductFromPage = () => {
    const btn =
      document.querySelector("[data-cmp-add][data-cmp-handle]") ||
      document.querySelector(".product-top-actions [data-cmp-add]");

    if (!btn) return null;

    return {
      handle: btn.getAttribute("data-cmp-handle"),
      title: btn.getAttribute("data-cmp-title"),
      image: btn.getAttribute("data-cmp-image"),
      url: btn.getAttribute("data-cmp-url"),
    };
  };

  // =========================
  // Compare Page render
  // =========================
  const renderComparePage = async () => {
    const root = $("[data-cmp-compare-page]");
    if (!root) return;

    try {
      const path = window.location.pathname || "";
      if (path.includes("/pages/compare")) {
        const ref = document.referrer || "";
        if (ref && !ref.includes("/pages/compare")) {
          sessionStorage.setItem(LAST_PAGE_KEY, ref);
        }
      }
    } catch {}

    const state = load();
    const cols = desiredCols(state.items.length);
    const selected = state.items.slice(0, cols);

    const products = await Promise.all(
      selected.map(async (it) => {
        if (!it?.handle) return null;
        try {
          const p = await fetch(`/products/${it.handle}.js`).then((r) => r.json());
          p.__cmpMetafields = await fetchMetafieldsFromProductHTML(it.handle);
          return p;
        } catch {
          return null;
        }
      })
    );

    const firstAvailableVariant = (p) => {
      if (!p?.variants?.length) return null;
      return p.variants.find((v) => v.available) || p.variants[0];
    };

    const variantKey = (handle) => `cmp_variant_${handle}`;
    const getChosenVariantId = (p) => {
      const h = p?.handle;
      if (!h) return null;
      const saved = sessionStorage.getItem(variantKey(h));
      const id = saved ? Number(saved) : null;
      const exists = p.variants?.some((v) => Number(v.id) === id);
      return exists ? id : null;
    };
    const setChosenVariantId = (handle, id) => {
      try {
        sessionStorage.setItem(variantKey(handle), String(id));
      } catch {}
    };

    const colHeads = [];
    const isSingle = state.items.length === 1;

    for (let i = 0; i < cols; i++) {
      const p = products[i];

      // ✅ if we only have 1 product, the other column(s) become clickable "add more"
      if (!p) {
        if (isSingle) {
          colHeads.push(`
            <div class="cmp-colhead cmp-colhead--empty">
              <div class="cmp-colleft">
                <div class="cmp-addslot__plus">＋</div>
                <div class="cmp-coltext">
                  <div class="cmp-empty-title">הוסף עוד מוצרים להשוואה</div>
                  <div class="cmp-empty-sub cmp-addslot__hint">לחץ כאן כדי לחזור למוצרים</div>
                </div>
              </div>
              <div class="cmp-xslot"></div>
            </div>
          `);
        } else {
          colHeads.push(`
            <div class="cmp-colhead cmp-colhead--empty">
              <div class="cmp-colleft">
                <div class="cmp-slot__img">➕</div>
                <div class="cmp-coltext">
                  <div class="cmp-empty-title">אנא בחר מוצר/ים להשוואה</div>
                  <div class="cmp-empty-sub">חזור למוצרים ובחר</div>
                </div>
              </div>
              <div class="cmp-xslot"></div>
            </div>
          `);
        }
        continue;
      }

      const url = escapeHtml(p.url || "#");
      const img = p.featured_image
        ? `<img class="cmp-thumb" src="${escapeHtml(p.featured_image)}" alt="">`
        : `<div class="cmp-slot__img">🧾</div>`;

      colHeads.push(`
        <div class="cmp-colhead">
          <div class="cmp-colleft">
            ${img}
            <div class="cmp-coltext">
              <a class="cmp-titlelink" href="${url}">${escapeHtml(p.title)}</a>
              <div class="cmp-vendor">${escapeHtml(p.vendor || "")}</div>
            </div>
          </div>

          <div class="cmp-xslot">
            <button
              type="button"
              class="cmp-x"
              data-cmp-remove-one
              data-handle="${escapeHtml(p.handle)}"
              aria-label="הסר מוצר מהשוואה"
              title="הסר מוצר"
            >✕</button>
          </div>
        </div>
      `);
    }

    const headerCells = colHeads
      .map((h, idx) => {
        // ✅ make empty slot clickable when only 1 product
        const isEmptyClickable = isSingle && (products[idx] == null);
        return `<td ${isEmptyClickable ? 'class="cmp-addslot" data-cmp-add-more' : ""}>${h}</td>`;
      })
      .join("");

    const rowSkuCells = [];
    const rowVendorCells = [];
    const rowVariantCells = [];
    const rowDescCells = [];
    const rowVisitCells = [];

    const shouldShowReadMore = (html) => {
      const text = String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
      return text.length > 220;
    };

    for (let i = 0; i < cols; i++) {
      const p = products[i];

      if (!p) {
        rowSkuCells.push(`<td>—</td>`);
        rowVendorCells.push(`<td>—</td>`);
        rowVariantCells.push(`<td>—</td>`);
        rowDescCells.push(`<td>—</td>`);
        rowVisitCells.push(`<td>—</td>`);
        continue;
      }

      const chosenId = getChosenVariantId(p) || firstAvailableVariant(p)?.id;
      const chosen = p.variants.find((v) => Number(v.id) === Number(chosenId)) || firstAvailableVariant(p);

      rowSkuCells.push(`<td>${escapeHtml(chosen?.sku || "—")}</td>`);
      rowVendorCells.push(`<td>${escapeHtml(p.vendor || "—")}</td>`);

      // ✅ hide options when only "Default Title"
      const hideVariantUI = isOnlyDefaultVariant(p);

      if (hideVariantUI) {
        rowVariantCells.push(`<td>-</td>`);
      } else {
        const variantsOptions = (p.variants || [])
          .map((v) => {
            const selectedAttr = Number(v.id) === Number(chosen?.id) ? " selected" : "";
            const disabledAttr = v.available ? "" : " disabled";
            return `<option value="${String(v.id)}"${selectedAttr}${disabledAttr}>
              ${escapeHtml(v.title)}${v.available ? "" : " (לא זמין)"}
            </option>`;
          })
          .join("");

        rowVariantCells.push(`
          <td>
            <div class="cmp-screen-only">
              <select class="cmp-variant-select" data-cmp-variant-select data-handle="${escapeHtml(p.handle)}">
                ${variantsOptions}
              </select>
            </div>

            <div class="cmp-print-only" style="font-weight:800;">
              ${escapeHtml(chosen?.title || "")}
            </div>
          </td>
        `);
      }

      const showToggle = shouldShowReadMore(p.description);
      rowDescCells.push(`
        <td>
          <div class="cmp-desc ${showToggle ? "cmp-desc--collapsed" : ""}" data-cmp-desc>${p.description || ""}</div>
        </td>
      `);

      const url = escapeHtml(p.url || "#");
      rowVisitCells.push(`
        <td>
          <a class="cmp-btn cmp-btn--primary cmp-btn--visit" href="${url}">ביקור במוצר</a>
        </td>
      `);
    }

    // ✅ dynamic metafields rows
    const allMetaLabels = [];
    const metaLabelSet = new Set();

    for (const p of products) {
      const mf = p?.__cmpMetafields || {};
      Object.keys(mf).forEach((label) => {
        const clean = normalizeLabel(label);
        if (!clean || shouldSkipLabel(clean)) return;
        if (metaLabelSet.has(clean)) return;
        metaLabelSet.add(clean);
        allMetaLabels.push(clean);
      });
    }

    const metaRowsHtml = allMetaLabels
      .map((label) => {
        const tds = Array.from({ length: cols }).map((_, i) => {
          const p = products[i];
          if (!p) return `<td>—</td>`;
          const mf = p.__cmpMetafields || {};
          const val = mf[label];
          return `<td>${val ? renderMetaValue(val) : "-"}</td>`;
        });

        return `
          <tr>
            <th class="cmp-rowlabel">${escapeHtml(label)}</th>
            ${tds.join("")}
          </tr>
        `;
      })
      .join("");

    const anyVariantsVisible = rowVariantCells.some((cell) => cell !== `<td>—</td>` && cell !== `<td>-</td>`);

    const tableHtml = `
      <div class="cmp-table-wrap">
        <table class="cmp-table">
          <tbody>
            <tr>
              <th class="cmp-rowlabel">מוצרים</th>
              ${headerCells}
            </tr>
            <tr>
              <th class="cmp-rowlabel">מק״ט</th>
              ${rowSkuCells.join("")}
            </tr>
            <tr>
              <th class="cmp-rowlabel">יצרן</th>
              ${rowVendorCells.join("")}
            </tr>

            ${metaRowsHtml}

            ${anyVariantsVisible ? `
            <tr>
              <th class="cmp-rowlabel">אפשרויות</th>
              ${rowVariantCells.join("")}
            </tr>` : ""}

            <tr class="cmp-row--desc">
              <th class="cmp-rowlabel">תיאור</th>
              ${rowDescCells.join("")}
            </tr>
            <tr>
              <th class="cmp-rowlabel">דף מוצר</th>
              ${rowVisitCells.join("")}
            </tr>
          </tbody>
        </table>
      </div>
    `;

    const cardsHtml = Array.from({ length: cols })
      .map((_, i) => {
        const p = products[i];

        if (!p) {
          // ✅ clickable card when only 1 product
          if (isSingle) {
            return `
              <div class="cmp-card cmp-addslot" data-cmp-add-more>
                <div class="cmp-slot--empty">
                  <div class="cmp-addslot__plus">＋</div>
                  <div>
                    <div class="cmp-empty-title">הוסף עוד מוצרים להשוואה</div>
                    <div class="cmp-empty-sub cmp-addslot__hint">לחץ כאן כדי לחזור למוצרים</div>
                  </div>
                </div>
              </div>
            `;
          }

          return `
            <div class="cmp-card">
              <div class="cmp-slot--empty">
                <div class="cmp-slot__img">➕</div>
                <div>
                  <div class="cmp-empty-title">אנא בחר מוצר/ים להשוואה</div>
                  <div class="cmp-empty-sub">חזור למוצרים ובחר</div>
                </div>
              </div>
            </div>
          `;
        }

        const chosenId = getChosenVariantId(p) || firstAvailableVariant(p)?.id;
        const chosen = p.variants.find((v) => Number(v.id) === Number(chosenId)) || firstAvailableVariant(p);

        const hideVariantUI = isOnlyDefaultVariant(p);

        const variantsOptions = (p.variants || [])
          .map((v) => {
            const selectedAttr = Number(v.id) === Number(chosen?.id) ? " selected" : "";
            const disabledAttr = v.available ? "" : " disabled";
            return `<option value="${String(v.id)}"${selectedAttr}${disabledAttr}>
              ${escapeHtml(v.title)}${v.available ? "" : " (לא זמין)"}
            </option>`;
          })
          .join("");

        const img = p.featured_image ? `<img class="cmp-thumb" src="${escapeHtml(p.featured_image)}" alt="">` : "";
        const url = escapeHtml(p.url || "#");
        const showToggle = shouldShowReadMore(p.description);

        return `
          <div class="cmp-card">
            <div class="cmp-card__head">
              <div class="cmp-colleft">
                ${img}
                <div class="cmp-coltext">
                  <a class="cmp-titlelink" href="${url}">${escapeHtml(p.title)}</a>
                  <div class="cmp-vendor">${escapeHtml(p.vendor || "")}</div>
                </div>
              </div>

              <div class="cmp-xslot">
                <button
                  type="button"
                  class="cmp-x"
                  data-cmp-remove-one
                  data-handle="${escapeHtml(p.handle)}"
                  aria-label="הסר מוצר מהשוואה"
                  title="הסר מוצר"
                >✕</button>
              </div>
            </div>

            <div class="cmp-kv">
              <div class="cmp-kv__row">
                <div class="cmp-kv__k">מק״ט</div>
                <div class="cmp-kv__v">${escapeHtml(chosen?.sku || "—")}</div>
              </div>

              <div class="cmp-kv__row">
                <div class="cmp-kv__k">יצרן</div>
                <div class="cmp-kv__v">${escapeHtml(p.vendor || "—")}</div>
              </div>

              <div class="cmp-kv__row">
                <div class="cmp-kv__k">אפשרויות</div>
                <div class="cmp-kv__v">
                  ${
                    hideVariantUI
                      ? `<div style="font-weight:900;">${money(p.price)}</div>`
                      : `
                        <select class="cmp-variant-select" data-cmp-variant-select data-handle="${escapeHtml(p.handle)}">
                          ${variantsOptions}
                        </select>
                        <div style="margin-top:8px; font-weight:900;">${money(p.price)}</div>
                      `
                  }
                </div>
              </div>

              <div class="cmp-kv__row">
                <div class="cmp-kv__k">תיאור</div>
                <div class="cmp-kv__v">
                  <div class="cmp-desc ${showToggle ? "cmp-desc--collapsed" : ""}" data-cmp-desc>${p.description || ""}</div>
                
                </div>
              </div>

              <div class="cmp-kv__row">
                <div class="cmp-kv__k">דף מוצר</div>
                <div class="cmp-kv__v">
                  <a class="cmp-btn cmp-btn--primary cmp-btn--visit" href="${url}">ביקור במוצר</a>
                </div>
              </div>
            </div>
          </div>
        `;
      })
      .join("");

    root.innerHTML = `
  <div class="cmp-print-area">
    <div class="cmp-page">
      <div class="cmp-page__head">
        <h1 style="margin:0;">השוואת מוצרים</h1>
        <div class="cmp-actions">
          <button class="cmp-btn" type="button" data-cmp-print>הדפסה</button>
          <button class="cmp-btn" type="button" data-cmp-undo-page aria-disabled="true">בטל</button>
          <button class="cmp-btn" type="button" data-cmp-redo-page aria-disabled="true">שחזר</button>
          <button class="cmp-btn" type="button" data-cmp-clear-page>נקה</button>
        </div>
      </div>

      ${tableHtml}

      <div class="cmp-mobile-grid">
        ${cardsHtml}
      </div>
    </div>
  </div>
`;


    // ✅ print
    root.querySelector("[data-cmp-print]")?.addEventListener("click", () => {
  printCompareClean();
});

    // ✅ read more
    $$("[data-cmp-desc-toggle]", root).forEach((btn) => {
      btn.addEventListener("click", () => {
        const wrap = btn.closest("td, .cmp-kv__v");
        const desc = wrap?.querySelector("[data-cmp-desc]");
        if (!desc) return;
        const collapsed = desc.classList.toggle("cmp-desc--collapsed");
        btn.textContent = collapsed ? "קרא עוד" : "קרא פחות";
      });
    });

    // ✅ variants change (only exists when not Default Title)
    $$("[data-cmp-variant-select]", root).forEach((sel) => {
      sel.addEventListener("change", () => {
        const handle = sel.getAttribute("data-handle");
        const id = Number(sel.value);
        if (!handle || !id) return;
        setChosenVariantId(handle, id);
        renderComparePage();
      });
    });

    // ✅ remove product
    $$("[data-cmp-remove-one]", root).forEach((btn) => {
      btn.addEventListener("click", () => {
        const handle = btn.getAttribute("data-handle");
        removeItemByHandle(handle);
        if ($("[data-cmp-compare-page]")) renderComparePage();
      });
    });

    // ✅ clear
    root.querySelector("[data-cmp-clear-page]")?.addEventListener("click", () => {
      clear();
      toast("נוקה");
      goBackOrHome();
    });

    // ✅ undo/redo page
    root.querySelector("[data-cmp-undo-page]")?.addEventListener("click", () => {
      if (!canUndo()) return;
      undo();
      renderComparePage();
    });

    root.querySelector("[data-cmp-redo-page]")?.addEventListener("click", () => {
      if (!canRedo()) return;
      redo();
      renderComparePage();
    });

    // ✅ clickable "add more" slots/cards when only 1 product
    $$("[data-cmp-add-more]", root).forEach((el) => {
      el.addEventListener("click", () => {
        toast("כדי להשוות צריך להוסיף עוד מוצרים — מחזיר אותך למוצרים");
        goBackOrHome();
      });
    });

    syncUndoRedoButtons();
  };

  // =========================
  // init
  // =========================
  document.addEventListener("DOMContentLoaded", () => {
    saveLastPage();

    ensureBar();
    bindAddButtons();
    syncAccountCompareButton();

    if ($("[data-cmp-compare-page]")) {
      injectComparePageStyles();
      renderComparePage();
    }

    syncUndoRedoButtons();
    updateBodyPad();
  });

  window.addEventListener("cmp:updated", () => {
    renderBar();
    syncAccountCompareButton();
    syncUndoRedoButtons();
    updateBodyPad();
  });

  window.addEventListener("cmp:undo_updated", () => syncUndoRedoButtons());
  window.addEventListener("cmp:redo_updated", () => syncUndoRedoButtons());

  document.addEventListener("shopify:section:load", () => {
    bindAddButtons();
  });
})();
